import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { exportsApi, type ExportFile } from '../../api/exports.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { datetime } from '../../lib/format.js';

/**
 * 이메일 명단 내려받기.
 *
 * 원본: marketing/email_download.php
 * DB 화면이 아니라 폴더에 쌓인 CSV 를 나열·내려받기·삭제한다.
 * 담긴 것이 이메일 명단이라 관리자 등급만 들어올 수 있고, 내려받기·삭제는
 * 누가 언제 했는지 서버 로그에 남는다 (원본에는 기록이 없었다).
 */

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function EmailExportPage() {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);

  const listing = useQuery({
    queryKey: ['exports', 'emails'],
    queryFn: () => exportsApi.listEmails(),
  });

  const remove = useMutation({
    mutationFn: (name: string) => exportsApi.deleteEmail(name),
    onSuccess: () => {
      setConfirming(null);
      void qc.invalidateQueries({ queryKey: ['exports', 'emails'] });
    },
  });

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            이메일다운
            <span className="badge badge--danger">관리자 전용</span>
          </h1>
          <p className="page-desc">
            내려받는 파일에는 <strong>회원 이메일 명단</strong>이 들어 있습니다.
            내려받기와 삭제는 기록이 남습니다.
          </p>
        </div>
      </header>

      {listing.isPending ? (
        <Spinner />
      ) : listing.isError ? (
        <ErrorBox error={listing.error} onRetry={() => void listing.refetch()} />
      ) : listing.data.files.length === 0 ? (
        <>
          <EmptyBox>내려받을 파일이 없습니다.</EmptyBox>
          <p className="page-note">
            찾아본 폴더: <code>{listing.data.dir}</code>
            {' '}— 다른 곳에 있으면 <code>EMAIL_EXPORT_DIR</code> 로 잡아주세요.
          </p>
        </>
      ) : (
        <>
          {remove.isError && <ErrorBox error={remove.error} />}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>파일</th>
                  <th style={{ textAlign: 'right' }}>크기</th>
                  <th>만든 시각</th>
                  <th style={{ textAlign: 'center' }}>내려받기</th>
                  <th style={{ textAlign: 'center' }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {listing.data.files.map((f: ExportFile) => (
                  <tr key={f.name}>
                    <td className="token-cell">{f.name}</td>
                    <td style={{ textAlign: 'right' }} className="td-muted">{size(f.size)}</td>
                    <td className="td-muted">{datetime(f.modifiedAt)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <a className="btn btn--ghost btn--sm" href={exportsApi.downloadUrl(f.name)}>
                        받기
                      </a>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {confirming === f.name ? (
                        <>
                          <button
                            type="button"
                            className="btn btn--sm"
                            onClick={() => remove.mutate(f.name)}
                            disabled={remove.isPending}
                          >
                            정말 삭제
                          </button>{' '}
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setConfirming(null)}
                          >
                            취소
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirming(f.name)}
                        >
                          삭제
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* 원본은 확인 없이 바로 지웠다. 되돌릴 수 없는 일이라 한 번 묻는다. */}
          <p className="page-note">삭제한 파일은 되돌릴 수 없습니다.</p>
        </>
      )}
    </section>
  );
}
